
































































































import { Component, Prop, Mixins } from "vue-property-decorator";
import RequestPromises from "@/mixins/requestPromises";
import { DataModules } from "@/store/modules/data";
import { IShare, IShareAction } from "@/models/share";
import Context from "@/mixins/context";
import AdminBrandId from "@/mixins/adminBrandId";
import i18n from "@/i18n";
import { ConfirmModalModes } from "@/data/enums";
import { IInvoice } from "@/models/invoices";
import { UpmindObjectTypes } from "@/data/enums/objects";

@Component
export default class ShareModal extends Mixins(
  RequestPromises,
  Context,
  AdminBrandId
) {
  // Props
  @Prop({ required: true })
  public objectId!: IInvoice["id"];

  @Prop({ default: UpmindObjectTypes.INVOICE })
  public objectType!: UpmindObjectTypes;

  @Prop({ default: true })
  public allowPayment!: boolean;

  @Prop({
    default: () =>
      i18n.t("_action.share_object", {
        object: i18n.t(`_.invoice`)
      })
  })
  public title!: string;

  @Prop({ default: () => i18n.t("_sentence.share_invoice_msg") })
  public message!: string;

  // Data
  public isLoading = false;
  public isShared = false;
  public showPrevious = false;
  public isProcessing = false;
  public isProcessingRegenarete = false;
  public currentShare?: IShare | null = null;
  public allActions: IShareAction[] = [
    {
      request: "GET",
      service: "invoice",
      method: "show_invoice"
    },
    {
      request: "GET",
      service: "invoice",
      method: "download_invoice_pdf"
    },
    {
      request: "POST",
      service: "payment",
      method: "store"
    }
  ];

  public form: { [key: string]: any; actions: IShareAction[] } = {
    own_access: 1,
    object_id: this.objectId,
    object_type: this.objectType,
    actions: [...this.allActions]
  };

  // Lifehooks
  public created() {
    this.isLoading = true;
    this.getShare().finally(() => (this.isLoading = false));
  }

  // Computed
  public get availableActions() {
    if (!this.allowPayment)
      return this.allActions.filter(
        i => i.service !== "payment" && i.method !== "store"
      );
    return this.allActions;
  }
  public get scope() {
    return `$share_${this.objectType}_${this.objectId}`;
  }

  public get generatedLink() {
    if (!this.currentShare) return "";
    const defaultOauthClient = this.$_.find(
      this.currentShare.oauth_clients,
      i => i.default
    );
    const origin = this.$_.get(defaultOauthClient, "origin");
    return `${origin}/${this.$_.lowerCase(this.objectType)}/${this.$_.get(
      this.currentShare,
      "token"
    )}`;
  }

  public get canDownload() {
    return !!this.$_.find(
      this.form.actions,
      i => i.service === "invoice" && i.method === "download_invoice_pdf"
    );
  }

  public get canPay() {
    return !!this.$_.find(
      this.form.actions,
      i => i.service === "payment" && i.method === "store"
    );
  }

  // Methods
  public onShare(isShared) {
    // Update old share
    if (this.currentShare) this.update({ revoked: !isShared });
    // Sharing first time
    else if (isShared) this.createShare();
    // Not shared, remove
    else this.deleteShare();
  }

  public copyToClipboard() {
    this.$copyText(this.generatedLink).then(() => {
      this.$buefy.toast.open({
        message: this.$t("_.copied_to_clipboard") as string
      });
    });
  }

  public onAllowDownload(canDownload) {
    this.addRemoveAction(canDownload, "invoice", "download_invoice_pdf");
    this.update();
  }

  public onAllowPayment(canPay) {
    this.addRemoveAction(canPay, "payment", "store");
    this.update();
  }

  public addRemoveAction(add, service, method) {
    if (add) {
      this.form.actions.push(
        this.allActions.find(
          i => i.service === service && i.method === method
        ) as IShareAction
      );
    } else {
      const index = this.$_.findIndex(
        this.form.actions,
        i => i.service === service && i.method === method
      );
      this.form.actions.splice(index, 1);
    }
  }

  public getShare() {
    return this.$store
      .dispatch(`data/${DataModules.SHARES}/list`, {
        vm: this,
        storeData: false,
        returnData: true,
        params: {
          "filter[object_id]": this.objectId,
          "filter[object_type]": this.objectType,
          with: "oauth_clients",
          limit: 1
        }
      })
      .then(result => {
        this.currentShare = this.$_.head(result) as IShare;
        this.isShared = this.currentShare ? !this.currentShare.revoked : false;
        if (this.currentShare)
          this.form.actions =
            this.currentShare.actions || this.availableActions;
      })
      .catch(error => {
        this.$store.dispatch("api/handleValidationError", error);
      })
      .finally(() => {
        this.isProcessing = false;
      });
  }

  public createShare() {
    this.isProcessing = true;

    const optionalFields = this.isAdmin ? { brand_id: this.brandId } : {};

    return this.$store
      .dispatch(`data/${DataModules.SHARES}/create`, {
        data: { ...this.form, ...optionalFields },
        vm: this
      })
      .then(result => {
        this.currentShare = result;
      })
      .catch(error => {
        this.$store.dispatch("api/handleValidationError", {
          error,
          vm: this
        });
      })
      .finally(() => {
        this.isProcessing = false;
      });
  }

  public update(data = {}) {
    this.isProcessing = true;
    return this.$store
      .dispatch(`data/${DataModules.SHARES}/update`, {
        token: this.$_.get(this.currentShare, "token"),
        data: {
          actions: this.form.actions,
          token: this.$_.get(this.currentShare, "token"),
          ...data
        },
        vm: this
      })
      .then(() => {
        // this.currentShare.actions = [...this.form.actions];
        // this.$_.set(this.currentShare, "actions", [...this.form.actions]);
        if (this.currentShare)
          this.currentShare.actions = [...this.form.actions];
        this.$buefy.toast.open({
          message: this.$t("_.share_settings_updated") as string
        });
      })
      .catch(error => {
        this.$store.dispatch("api/handleValidationError", {
          error,
          vm: this
        });
      })
      .finally(() => {
        this.isProcessing = false;
      });
  }

  public deleteShare() {
    if (!this.currentShare) return;
    return this.$store
      .dispatch(`data/${DataModules.SHARES}/delete`, {
        token: this.$_.get(this.currentShare, "token"),
        vm: this
      })
      .then(() => {
        this.currentShare = null;
        this.isShared = false;
      })
      .catch(error => {
        this.$store.dispatch("api/handleError", error);
      });
  }

  public async confirmRegenerate() {
    const confirm = await this.$store.dispatch("ui/open/confirmModal", {
      config: {
        props: {
          mode: ConfirmModalModes.BUTTON,
          autoClose: false,
          confirmButtonText: this.$t("_action.regenerate_link"),
          message: this.$t(`_sentence.confirm_regenerate`)
        },
        events: {
          confirmed: async () => {
            await this.regenerateLink();
            confirm.close();
          }
        }
      }
    });
  }

  public async regenerateLink() {
    try {
      this.isProcessingRegenarete = true;
      await this.deleteShare();
      await this.createShare();
      await this.getShare();
    } finally {
      this.isProcessingRegenarete = false;
    }
  }
}
